import type React from 'react';

export type MobileTab = 'read' | 'chapters' | 'chat';

interface MobileTabBarProps {
  activeTab: MobileTab;
  onTabChange: (tab: MobileTab) => void;
  hasSelection: boolean;
}

export function MobileTabBar({ activeTab, onTabChange, hasSelection }: MobileTabBarProps) {
  const tabs: Array<{
    id: MobileTab;
    label: string;
    panelId: string;
    buttonId: string;
    icon: React.ReactNode;
  }> = [
    {
      id: 'read',
      label: 'Read',
      panelId: 'studio-content-pane',
      buttonId: 'tab-read',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
    },
    {
      id: 'chapters',
      label: 'Chapters',
      panelId: 'studio-sidebar-pane',
      buttonId: 'tab-chapters',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25H12" />
        </svg>
      ),
    },
    {
      id: 'chat',
      label: 'AI Chat',
      panelId: 'studio-chat-pane',
      buttonId: 'tab-chat',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
        </svg>
      ),
    },
  ];

  return (
    <div
      role="tablist"
      aria-label="Book studio navigation"
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden bg-white border-t border-stone-200 flex"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {tabs.map(tab => {
        const isActive = activeTab === tab.id;
        const showBadge = tab.id === 'chat' && hasSelection && !isActive;
        return (
          <button
            key={tab.id}
            id={tab.buttonId}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={tab.panelId}
            onClick={() => onTabChange(tab.id)}
            className={`flex-1 flex flex-col items-center justify-center gap-1 min-h-[44px] py-2 transition-colors ${
              isActive ? 'text-amber-600' : 'text-stone-400 hover:text-stone-600'
            }`}
          >
            <span className="relative">
              {tab.icon}
              {showBadge && (
                <>
                  <span
                    className="absolute -top-0.5 -right-0.5 bg-amber-500 rounded-full w-1.5 h-1.5"
                    aria-hidden="true"
                  />
                  <span className="sr-only">AI Chat — text selected</span>
                </>
              )}
            </span>
            <span className="text-[11px] font-semibold tracking-tight">{tab.label}</span>
          </button>
        );
      })}
    </div>
  );
}
